import {
  type FC,
  type SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react'

import './_verificationCode.scss'
import {
  type E164Number,
  isValidPhoneNumber,
  parsePhoneNumber
} from 'libphonenumber-js'
import { useTranslation } from 'react-i18next'
import PhoneInput from 'react-phone-input-2'
import { useTimer } from 'react-timer-hook'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { sendDeleteCode } from 'services/endpoints/profile'
import { useUserProfile } from 'store'

import 'react-phone-input-2/lib/style.css'
import { setSMSVerificationCode } from './Verification'

const SMSVerificationCode: FC<{
  changeFn?: (val: string) => void
  setCodeFilled?: (val: boolean) => void
  isAppAuthentificator?: boolean
  setOtpResponse?: (val: any) => void
  otpResponse?: any
  setInputValue?: (val: string) => void
  inputValue?: string
}> = ({
  changeFn,
  isAppAuthentificator,
  setCodeFilled,
  setOtpResponse,
  otpResponse,
  setInputValue
}) => {
      const userData = useUserProfile()
      const { t } = useTranslation()
      const [sending, setSending] = useState(false)
      const [phone, setPhone] = useState<string | undefined>()
      const [validNumber, setValidNumber] = useState(false)
      const [preferredCountries, setPreferredCountries] = useState(['us', 'in'])
      const [allCountryCodes, setAllCountryCodes] = useState<string[]>([])
      const [detectedCountry, setDetectedCountry] = useState<string>('')
      const [codeSent, setCodeSent] = useState<boolean>(false)
      const locationRef = useRef(false)
      const hasFetchedRef = useRef(false)
      const [code, setCode] = useState<any>({
          digit1: '',
          digit2: '',
          digit3: '',
          digit4: '',
          digit5: '',
          digit6: ''
      })
      useEffect(() => {
          if (!window.location.pathname.includes('/sms-verification')) return
          if (hasFetchedRef.current) return
          hasFetchedRef.current = true

          // Fetch all country codes
          fetch('https://restcountries.com/v3.1/all?fields=cca2')
              .then(async res => {
                  if (!res.ok) throw new Error('Failed to fetch country codes')
                  return await res.json()
              })
              .then(data => {
                  const codes = data
                      .map((country: { cca2: string }) => country.cca2?.toLowerCase())
                      .filter(Boolean)
                  setAllCountryCodes(codes)
              })
              .catch(err => {
                  console.error('Error fetching country data:', err)
              })
      }, [])

      useEffect(() => {
          if (!window.location.pathname.includes('/sms-verification')) return
          if (locationRef.current) return
          locationRef.current = true
          fetch('https://ipapi.co/json/')
              .then(async res => await res.json())
              .then(data => {
                  const countryCode = data?.country?.toLowerCase()
                  if (countryCode) {
                      setDetectedCountry(countryCode)
                      setPreferredCountries([countryCode, 'us', 'in']) // prioritize detected
                  }
              })
              .catch(err => {
                  console.error('Error detecting location:', err)
              })
      }, [])

      const handleChange = (value: string) => {
          const formattedNumber = value.startsWith('+') ? value : `+${value}`
          setPhone(formattedNumber)
          try {
              const parsed = parsePhoneNumber(formattedNumber)
              // const isValid = isValidPhoneNumber(formattedNumber)
              // console.log('isValid: ', isValid);
              setValidNumber(parsed.isValid())
          } catch (error) {
              setValidNumber(false)
          }
      }

      const GetOtp = async (phone: string) => {
          const response = await setSMSVerificationCode(phone)
          if (response.error) {
              addToast('error', response.error)
              return
          }
          setOtpResponse?.(response.reference)
          addToast('success', 'OTP sent successfully')
          return response
      }

      useEffect(() => {
          if (changeFn) {
              changeFn(
                  `${code.digit1}${code.digit2}${code.digit3}${code.digit4}${code.digit5}${code.digit6}`
              )
          }
          setInputValue?.(Object.values(code).join(''))
      }, [code])

      const { seconds, minutes, restart } = useTimer({
          expiryTimestamp: new Date(), // dummy initial value, gets replaced in useEffect
          onExpire: () => {
              setCodeSent(false)
          }
      })
      const formatTime = (min: number, sec: number) =>
          `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`

      const updateCode = (
          key: string,
          value: number,
          next?: React.RefObject<HTMLInputElement>
      ) => {
          if (value) {
              setCode({ ...code, [key]: value })
              next && next.current!.focus()
          } else {
              setCode({ ...code, [key]: '' })
          }
      }
      useEffect(() => {
          const allFilled = Object.values(code).every(d => d !== '')
          setCodeFilled?.(allFilled)
      }, [code, setCodeFilled])

      const handleKeyDown = (
          e: React.KeyboardEvent<HTMLInputElement>,
          currentDigit: keyof typeof code,
          prevRef: React.RefObject<HTMLInputElement> | null
      ) => {
          if (
              e.key === 'Backspace' &&
              code[currentDigit] === '' &&
              prevRef?.current
          ) {
              prevRef.current.focus()
          }
      }

      const dig1Ref = useRef<HTMLInputElement>(null)
      const dig2Ref = useRef<HTMLInputElement>(null)
      const dig3Ref = useRef<HTMLInputElement>(null)
      const dig4Ref = useRef<HTMLInputElement>(null)
      const dig5Ref = useRef<HTMLInputElement>(null)
      const dig6Ref = useRef<HTMLInputElement>(null)

      const inputBlocks = [
          {
              id: 1,
              type: 'text',
              value: code.digit1,
              changeFn: (val: any) => {
                  updateCode('digit1', val, dig2Ref)
              },
              ref: dig1Ref,
              prevRef: null
          },
          {
              id: 2,
              type: 'text',
              value: code.digit2,
              changeFn: (val: any) => {
                  updateCode('digit2', val, dig3Ref)
              },
              ref: dig2Ref,
              prevRef: dig1Ref
          },
          {
              id: 3,
              type: 'text',
              value: code.digit3,
              changeFn: (val: any) => {
                  updateCode('digit3', val, dig4Ref)
              },
              ref: dig3Ref,
              prevRef: dig2Ref
          },
          {
              id: 4,
              type: 'text',
              value: code.digit4,
              changeFn: (val: any) => {
                  updateCode('digit4', val, dig5Ref)
              },
              ref: dig4Ref,
              prevRef: dig3Ref
          },
          {
              id: 5,
              type: 'text',
              value: code.digit5,
              changeFn: (val: any) => {
                  updateCode('digit5', val, dig6Ref)
              },
              ref: dig5Ref,
              prevRef: dig4Ref
          },
          {
              id: 6,
              type: 'text',
              value: code.digit6,
              changeFn: (val: any) => {
                  updateCode('digit6', val)
              },
              ref: dig6Ref,
              prevRef: dig5Ref
          }
      ]

      return (
          <div className={` ${isAppAuthentificator ? 'verificationCode' : ''}`}>
              {!isAppAuthentificator && (
                  <>
                      <div className='verificationCode__actions__input'>
                          <p className='verificationCode__title'>
                              {t('Enter Mobile Number')}
                          </p>
                          <InputBox>
                              <PhoneInput
                                  country={detectedCountry}
                                  value={phone as E164Number}
                                  onChange={(value: E164Number) => handleChange(value)}
                                  onlyCountries={allCountryCodes}
                                  preferredCountries={preferredCountries}
                                  inputStyle={{ width: '100%' }}
                                  placeholder="Enter mobile number"
                                  enableSearch={true}
                                  disableSearchIcon={true}
                              />
                          </InputBox>
                      </div>
                      <div className='verificationCode__actions'>
                          {codeSent ? (
                              <div className='verificationCode__actions__codesent'>
                                  {t('codeWasSentTo')} {''}
                                  {/* <span>{userData?.email}</span> */}
                                  <span>{phone}</span>
                                  <br />
                                  {t('reSendSms')}{' '}
                                  <span>{formatTime(minutes, seconds)}</span>
                              </div>
                          ) : (
                              <Button
                                  text={t('sendCode')}
                                  color='black'
                                  font='mont-14-normal'
                                  width='13'
                                  height='3'
                                  disabled={!validNumber || sending}
                                  clickFn={() => {
                                      setSending(true)
                                      if (phone) {
                                          GetOtp(phone)
                                              .then(() => {
                                                  const newTime = new Date()
                                                  newTime.setMinutes(
                                                      newTime.getMinutes() + 5
                                                  )
                                                  restart(newTime)
                                                  setCodeSent(true)
                                                  setSending(false)
                                              })
                                              .catch(() => {
                                                  setSending(false)
                                                  addToast(
                                                      'error',
                                                      t('errorWhileSendingCode')
                                                  )
                                              })
                                      } else {
                                          addToast(
                                              'error',
                                              'Phone number is required'
                                          )
                                      }
                                  }}
                                  customClass='verificationCode__actions__button'
                              />
                          )}
                      </div>
                  </>
              )}
              <p className='verificationCode__title'>{t('verificationCode')}</p>
              <div className='verificationCode__input'>
                  {inputBlocks.map((block: any, key: number) => (
                      <input
                          key={key}
                          type={block.type}
                          value={block.value}
                          maxLength={1}
                          onChange={(e: any) =>
                              block.changeFn(e.currentTarget.value)
                          }
                          onKeyDown={e =>
                              handleKeyDown(
                                  e,
                                  `digit${block.id}` as keyof typeof code,
                                  block.prevRef
                              )
                          }
                          ref={block.ref}
                          className={`verificationCode__input__block${block.value
                              ? ' verificationCode__input__block--filled'
                              : ''
                              }`}
                      />
                  ))}
              </div>
          </div>
      )
  }

export default SMSVerificationCode

const InputBox = styled.div`
  margin-bottom: 1rem;
  margin-top: 1rem;

  .react-tel-input {
      width: 100% !important;
      position: relative;

      input.form-control {
          width: 100%;
          height: 50px;
          padding: 16px 100px 16px 60px;
          border: 0.2rem solid #e2e2e2;
          border-radius: 10px;
          font-size: 1.5rem;
          font-weight: 600;
          outline: 0;
          color: #0d1444;
          background: #fff;
          transition: border-color 0.3s;

          &:-webkit-autofill {
              box-shadow: 0 0 0 3rem #f6f6f6 inset !important;
              -webkit-box-shadow: 0 0 0 3rem #f6f6f6 inset !important;
              -webkit-text-fill-color: #0d1444 !important;
          }

          &:focus {
              border: 0.2rem solid #4baaf9;
              outline: none;
          }
      }

      .flag-dropdown {
          position: absolute;
          left: 10px;
          border: none;
          background: none;
      }
      .selected-flag {
          padding-left: 16px;
          border: none;
          background: none;
      }

      .flag-dropdown.open .selected-flag {
          background: none;
      }

      .country-list {
          max-height: 280px;
          overflow-y: auto;
          margin-top: 8px;
          border-radius: 1rem;
          box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
          border: 1px solid #eaeaea;
          font-size: 1.5rem;
          background: #fff;
          z-index: 9999;

          .country {
              padding: 10px 15px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              cursor: pointer;
              transition: background 0.2s ease;

              &:hover {
                  background: #f0f0f0;
              }

              .country-name {
                  flex: 1;
                  font-weight: 500;
                  color: #333;
              }

              .dial-code {
                  font-weight: bold;
                  color: #555;
              }
          }

          .search {
              padding: 10px;
              border-bottom: 1px solid #eee;
              display: flex;
              align-items: center;
              height: 60px;

              .search-box {
                  height: 100%;
                  width: 100%;
                  padding: 6px 10px;
                  border-radius: 8px;
                  border: 1px solid #ccc;
                  font-size: 1.9rem;
                  outline: none;

                  &:focus {
                      border-color: #999;
                  }
              }
          }

          .divider {
              height: 1px;
              background: #eee;
              margin: 5px 0;
          }
      }
  }
`
